/**
 * TradingView chart URLs / widgets use exchange codes that often differ from
 * Yahoo Finance `exchange` strings or our DB defaults (e.g. Cboe BZX ETFs → CBOE, not NASDAQ).
 */

/** Ticker → TradingView prefix when DB/Yahoo exchange is wrong or missing. */
const TRADINGVIEW_TICKER_OVERRIDES: Record<string, string> = {
  // Cboe-listed ETF; Yahoo/DB often tag as NASDAQ - TV is CBOE:JEMA
  JEMA: 'CBOE',
  // Common ETFs: TV prefixes differ from display names
  SPY: 'AMEX',
  QQQ: 'NASDAQ',
};

/**
 * Map stored or Yahoo-style exchange strings to a TradingView symbol prefix.
 */
function normalizeExchangeToTradingView(exchangeRaw: string): string | null {
  const e = exchangeRaw.trim().toUpperCase();
  if (!e) return null;

  if (e.includes('CBOE') || e === 'BTS' || e === 'BZX' || e.includes('BATS')) {
    return 'CBOE';
  }
  if (e.includes('ARCA') || e === 'PCX' || e === 'NYSEARCA') {
    return 'AMEX';
  }
  if (e === 'NMS' || e === 'NASDAQ' || e === 'NGM' || e === 'NCM') {
    return 'NASDAQ';
  }
  if (e === 'NYQ' || e === 'NYSE') {
    return 'NYSE';
  }
  if (e === 'ASE' || e === 'AMEX' || e.includes('NYSE MKT') || e.includes('NYSE AMERICAN')) {
    return 'AMEX';
  }
  if (e === 'CBOE') {
    return 'CBOE';
  }
  if (e === 'NASDAQ' || e === 'NYSE' || e === 'AMEX') {
    return e;
  }
  return null;
}

export function getTradingViewExchangePrefix(args: {
  ticker: string;
  exchange?: string | null;
}): string {
  const t = args.ticker.trim().toUpperCase();
  const fromOverride = TRADINGVIEW_TICKER_OVERRIDES[t];
  if (fromOverride) return fromOverride;

  const ex = args.exchange?.trim() || '';
  const normalized = normalizeExchangeToTradingView(ex);
  if (normalized) return normalized;

  if (ex) {
    const up = ex.toUpperCase();
    if (['NASDAQ', 'NYSE', 'AMEX', 'CBOE'].includes(up)) return up;
  }

  // No exchange data - return empty so TradingView auto-resolves the symbol
  return '';
}
